import debounce from 'lodash.debounce';
import React, { useState } from 'react';

import { noop } from '@utils';
import Icon from '../Icon';
import InputRange from '../InputRange';
import ToolbarButton from '../ToolbarButton';

import './CinePlayer.css';

export type CinePlayerProps = {
	className: string;
	isPlaying: boolean;
	minCineRate?: number;
	maxCineRate?: number;
	stepCineRate?: number;
	cineRate?: number;
	onCineRateChange: (isPlaying: boolean, value: number) => void;
	onSkipback: () => void;
	onClose: () => void;
	disabled?: boolean;
};

const CinePlayer: React.FC<CinePlayerProps> = ({
	className,
	isPlaying = false,
	minCineRate = 1,
	maxCineRate = 90,
	stepCineRate = 1,
	cineRate = 24,
	onCineRateChange = noop,
	onSkipback = noop,
	disabled = false,
}) => {
	const [cineRateState, setCineRateState] = useState(cineRate);
	const debouncedSetCineRate = debounce(onCineRateChange, 300);

	const getPlayPauseIconName = () => {
		if (disabled) {
			return 'icon-play';
		}
		return isPlaying ? 'icon-pause' : 'icon-play';
	};

	const handleSetCineRate = (cineRate: number) => {
		if (cineRate < minCineRate || cineRate > maxCineRate) {
			return;
		}
		setCineRateState(cineRate);
		debouncedSetCineRate(isPlaying, cineRate);
	};

	const fpsButtonClassNames = `${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}  text-white w-4 flex items-center justify-center`;

	return (
		<div className={className}>
			<div className="flex flex-col items-center gap-2">
				<div className={'flex items-center'}>
					<ToolbarButton
						icon={'skipback'}
						onInteraction={() => {
							if (!disabled) {
								onSkipback();
							}
						}}
						dropdownContent="null"
						id="skipback"
						type="action"
						disabled={disabled && true}
						size="quickToolbar"
					/>
					<ToolbarButton
						icon={getPlayPauseIconName()}
						onInteraction={() => {
							if (!disabled) {
								onCineRateChange(!isPlaying, cineRateState);
							}
						}}
						dropdownContent="null"
						id="play"
						type="action"
						disabled={disabled && true}
						size="quickToolbar"
					/>

					<div className="flex h-6 items-stretch gap-1 rounded border border-common-light mx-2">
						<div
							className={`${fpsButtonClassNames} rounded-l`}
							onClick={() => {
								if (!disabled) {
									handleSetCineRate(cineRateState - 1);
								}
							}}
						>
							<Icon name="arrow-left-small" />
						</div>
						<div className="select-none w-11 text-center text-sm leading-[22px] text-white group-hover/fps:text-primary-light">
							{`${cineRateState} FPS`}
						</div>
						<div
							className={`${fpsButtonClassNames} rounded-r`}
							onClick={() => {
								if (!disabled) {
									handleSetCineRate(cineRateState + 1);
								}
							}}
						>
							<Icon name="arrow-right-small" />
						</div>
					</div>
				</div>
				<div className="w-full px-2">
					<InputRange
						inputClassName="w-full select-none"
						value={cineRateState}
						minValue={minCineRate}
						maxValue={maxCineRate}
						step={stepCineRate}
						onChange={handleSetCineRate}
						showLabel={false}
						disabled={disabled}
					/>
				</div>
			</div>
		</div>
	);
};

export default CinePlayer;
